import axios from 'axios';
import {
  sanitizeUrlForTelemetry,
  trackBackendRequestFailure,
  trackSanitizedException,
} from 'src/observability/appInsights';
import { clearAccessToken, getAccessToken, setAccessToken } from './authTokenStore';
import { refreshAccessToken } from './authSession';

const axiosServices = axios.create({
  withCredentials: true,
});
const frontendUrl = import.meta.env.VITE_FRONTEND_URL || '';

const clearStaleAuthState = () => {
  const localKeysToClear = [
    'jwtToken',
    'activeWorkspaceId',
    'previousActiveWorkspaceId',
    'acceptShareRequestUrl',
  ];
  localKeysToClear.forEach((key) => localStorage.removeItem(key));

  const sessionKeysToClear = ['username', 'password'];
  sessionKeysToClear.forEach((key) => sessionStorage.removeItem(key));
  clearAccessToken();
};

axiosServices.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers.Accept = 'application/json';
    const workspaceId = localStorage.getItem('activeWorkspaceId');
    if (workspaceId) {
      config.headers['X-Workspace-ID'] = workspaceId;
    }
    return config;
  },
  (error) => {
    trackSanitizedException(error, {
      source: 'axios.request',
    });
    return Promise.reject(error);
  },
);

axiosServices.interceptors.response.use(
  async (response) => response,
  async (error) => {
    const requestUrl = sanitizeUrlForTelemetry(error?.config?.url);
    const requestMethod = error?.config?.method || 'get';
    const status = error?.response?.status;

    trackBackendRequestFailure('axios.response', requestUrl, requestMethod, status);
    trackSanitizedException(error, {
      source: 'axios.response',
      method: requestMethod,
      status,
      url: requestUrl,
    });

    const detail = error.response?.data?.detail;
    const code = detail?.code || error.response?.data?.code;
    const originalRequest = error.config;

    if (error.response && error.response.status === 403 && code === 'PASSWORD_EXPIRED') {
      clearStaleAuthState();
      window.location.href = `${frontendUrl}/forget-password`;
      return Promise.reject(error);
    }

    if (error.response && error.response.status === 403 && code === 'MFA_SETUP_REQUIRED') {
      const onMfaSetupPage = globalThis.location.pathname === '/mfa-setup';
      if (!onMfaSetupPage) {
        globalThis.location.href = `${frontendUrl}/mfa-setup`;
      }
      return Promise.reject(error);
    }

    if (
      error.response &&
      error.response.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !String(originalRequest.url || '').includes('/auth/refresh')
    ) {
      originalRequest._retry = true;
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        originalRequest.headers.Authorization = `Bearer ${refreshed}`;
        return axiosServices(originalRequest);
      }
    }

    if (error.response && error.response.status === 401) {
      trackBackendRequestFailure('axios.auth_redirect', requestUrl, requestMethod, status);
      const actorToken = localStorage.getItem('superAdminActorToken');
      const wasImpersonating = Boolean(localStorage.getItem('superAdminImpersonationContext'));
      clearStaleAuthState();
      const signInPath =
        code === 'AUTH_SESSION_IDLE_TIMEOUT'
          ? `${frontendUrl}/signin?reason=idle`
          : `${frontendUrl}/signin`;
      if (actorToken && wasImpersonating) {
        setAccessToken(actorToken);
        window.location.href = frontendUrl || '/';
      } else {
        window.location.href = signInPath;
      }
    }
    return Promise.reject(
      (error.response && { ...error?.response?.data, status: error.response.status }) ||
        'Wrong Services',
    );
  },
);

export default axiosServices;
